import React, { useState } from 'react';
import { useField } from 'formik';
import { Box, Grid, InputAdornment, MenuItem, TextField, Typography } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import IconAction from '../common/IconAction';

/**
 * Formik-aware inputs. Each one wires value, change, blur and the error state
 * so pages only declare the field name, label and any constraints.
 */

const useFieldState = (name, helperText) => {
  const [field, meta, helpers] = useField(name);
  const error = meta.touched && meta.error ? meta.error : null;
  return { field, helpers, error, helperText: error || helperText };
};

export const TextInput = ({ name, helperText, ...props }) => {
  const { field, error, helperText: help } = useFieldState(name, helperText);
  return (
    <TextField
      {...field}
      value={field.value ?? ''}
      error={Boolean(error)}
      helperText={help}
      fullWidth
      {...props}
    />
  );
};

export const NumberInput = ({ name, unit, step = 'any', min, max, helperText, inputProps, InputLabelProps, ...props }) => {
  const { field, error, helperText: help } = useFieldState(name, helperText);
  return (
    <TextField
      {...field}
      value={field.value ?? ''}
      type="number"
      error={Boolean(error)}
      helperText={help}
      fullWidth
      // Keep the label floated when a unit is shown so the two never overlap in narrow fields.
      InputLabelProps={{ shrink: unit ? true : undefined, ...InputLabelProps }}
      inputProps={{ step, min, max, inputMode: 'decimal', ...inputProps }}
      InputProps={{
        endAdornment: unit ? (
          <InputAdornment position="end">
            <Typography variant="body2" color="text.secondary">
              {unit}
            </Typography>
          </InputAdornment>
        ) : undefined,
      }}
      {...props}
    />
  );
};

export const SelectInput = ({ name, options, placeholder = 'Select…', helperText, ...props }) => {
  const { field, error, helperText: help } = useFieldState(name, helperText);
  return (
    <TextField
      {...field}
      value={field.value ?? ''}
      select
      error={Boolean(error)}
      helperText={help}
      fullWidth
      SelectProps={{ displayEmpty: true }}
      InputLabelProps={{ shrink: true }}
      {...props}
    >
      <MenuItem value="">
        {/* variant="inherit": body text's taller line height would make an empty select taller than the other fields. */}
        <Typography component="span" variant="inherit" color="text.secondary">
          {placeholder}
        </Typography>
      </MenuItem>
      {options.map((option) => (
        <MenuItem key={option.value} value={option.value}>
          {option.label}
        </MenuItem>
      ))}
    </TextField>
  );
};

export const DateInput = (props) => <TextInput type="date" InputLabelProps={{ shrink: true }} {...props} />;

/** Password field with a show/hide toggle. `actions` adds extra buttons before the toggle. */
export const PasswordInput = ({ actions, ...props }) => {
  const [visible, setVisible] = useState(false);
  return (
    <TextInput
      type={visible ? 'text' : 'password'}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            {actions}
            <IconAction
              label={visible ? 'Hide password' : 'Show password'}
              onClick={() => setVisible((v) => !v)}
              edge="end"
              icon={visible ? <VisibilityOff /> : <Visibility />}
            />
          </InputAdornment>
        ),
      }}
      {...props}
    />
  );
};

/** Groups related fields under a heading, separated from the previous section by a rule. */
export const FormSection = ({ title, description, children, spacing = 2.5 }) => (
  <Box sx={{ '& + &': { mt: 4, pt: 4, borderTop: 1, borderColor: 'divider' } }}>
    {(title || description) && (
      <Box sx={{ mb: 2.5 }}>
        {title && (
          <Typography variant="h6" component="h2">
            {title}
          </Typography>
        )}
        {description && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {description}
          </Typography>
        )}
      </Box>
    )}
    <Grid container spacing={spacing}>
      {children}
    </Grid>
  </Box>
);

/** A grid cell inside a FormSection. Defaults to full width on phones, half on larger screens. */
export const FieldCell = ({ children, xs = 12, sm = 6, md }) => (
  <Grid item xs={xs} sm={sm} md={md}>
    {children}
  </Grid>
);
